import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Switch } from "@/components/ui/switch";
import {
  notificationSettingsApi,
  notificationSettingsQueryKeys,
  type NotificationEventRow,
  type NotificationSettings,
} from "../../shared/api/notificationSettings";
import { apiError } from "../../shared/api/client";
import { ChannelConnectionLine, useMessagingChannels } from "./ChannelConnection";

type Key = "zaloEnabled" | "smsEnabled" | "emailEnabled";

const CHANNELS: { key: Key; channel: string; label: string; hint: string }[] = [
  {
    key: "zaloEnabled",
    channel: "ZBS",
    // #19667 D5 — one name for the channel everywhere ("Zalo ZBS"), as the table and Addy Message say.
    label: "Zalo ZBS",
    hint: "Ưu tiên cao nhất — cần Zalo OA và bệnh nhân đồng ý nhận tin.",
  },
  {
    key: "smsEnabled",
    channel: "SMS",
    label: "SMS",
    hint: "Cần nhà cung cấp SMS và bệnh nhân đồng ý nhận liên hệ.",
  },
  {
    key: "emailEnabled",
    channel: "EMAIL",
    label: "Email",
    hint: "Gửi tới địa chỉ email của bệnh nhân.",
  },
];

/**
 * "8/10 sự kiện sẵn sàng" for one channel (#19577) — among the switched-on patient events that use
 * it, how many could go out on it right now. Read from the per-channel readiness the event list
 * already carries; no extra request.
 */
export function channelSummary(
  channel: string,
  events: NotificationEventRow[],
): { ready: number; total: number } {
  let ready = 0;
  let total = 0;
  for (const ev of events) {
    if (ev.audience === "STAFF_ROLE" || !ev.enabled) continue;
    const r = ev.channelReadiness?.[channel];
    if (!r || r === "DISABLED") continue;
    total++;
    if (r === "READY") ready++;
  }
  return { ready, total };
}

/**
 * The clinic's three channel switches (#19577), saved on click like everything else on the page.
 *
 * <p>They used to be held in a form and saved by a "Lưu thay đổi" button at the bottom of a
 * 3,000-pixel page, while every event and template above it saved instantly — so a clinic could
 * tick SMS, scroll away and leave with nothing saved. One rule for the whole page now.
 *
 * <p>The API replaces the whole settings object, so every switch is locked while one save is in
 * flight: two quick clicks would otherwise each send a stale copy and the second would undo the
 * first. A refused save simply leaves the switch showing what the server holds.
 */
export function ChannelSwitches({
  settings,
  events,
}: {
  settings: NotificationSettings;
  events: NotificationEventRow[];
}) {
  const qc = useQueryClient();
  // #19613 — whether each channel is connected at all, beside the clinic's own switch: a switch
  // that is on for a channel Addy Message cannot send on must not read as "ready".
  const connection = useMessagingChannels();
  const [pending, setPending] = useState<{ key: Key; value: boolean } | null>(
    null,
  );
  const [note, setNote] = useState<{
    key: Key;
    ok: boolean;
    text: string;
  } | null>(null);

  // "Đã lưu" is a confirmation, not a state: it fades after a moment. An error stays.
  useEffect(() => {
    if (!note?.ok) return;
    const t = window.setTimeout(() => setNote(null), 3000);
    return () => window.clearTimeout(t);
  }, [note]);

  const save = useMutation({
    mutationFn: (v: { key: Key; value: boolean }) =>
      notificationSettingsApi.update({ ...settings, [v.key]: v.value }),
    onSuccess: (s, v) => {
      qc.setQueryData(notificationSettingsQueryKeys.all(), s);
      // Turning a channel on or off changes every event's readiness (#19209).
      qc.invalidateQueries({
        queryKey: notificationSettingsQueryKeys.events(),
      });
      setNote({ key: v.key, ok: true, text: "Đã lưu" });
    },
    onError: (e, v) =>
      setNote({ key: v.key, ok: false, text: apiError(e).detail }),
    onSettled: () => setPending(null),
  });

  return (
    <div className="grid gap-3">
    {connection.data?.actionable && (
      <p role="alert" className="rounded border border-amber-300 bg-amber-50 p-2 text-xs text-amber-900">
        ⚠️ {connection.data.actionable}
      </p>
    )}
    <div className="grid gap-3 sm:grid-cols-3" data-testid="channel-switches">
      {CHANNELS.map((c) => {
        const checked =
          pending?.key === c.key ? pending.value : settings[c.key];
        const { ready, total } = channelSummary(c.channel, events);
        return (
          <div key={c.key} className="grid gap-1 rounded-md border p-3">
            <label className="flex cursor-pointer items-center justify-between gap-3">
              <span className="text-sm font-medium">{c.label}</span>
              <Switch
                checked={checked}
                disabled={save.isPending}
                aria-label={c.label}
                onCheckedChange={(value) => {
                  setNote(null);
                  setPending({ key: c.key, value });
                  save.mutate({ key: c.key, value });
                }}
              />
            </label>
            <span className="text-xs text-muted-foreground">{c.hint}</span>
            <ChannelConnectionLine
              line={connection.data?.channels.find((l) => l.channel === c.channel)}
            />
            <span
              className={
                "text-xs " +
                (!checked
                  ? "text-muted-foreground"
                  : total > 0 && ready < total
                    ? "text-amber-800"
                    : "text-emerald-700")
              }
              data-testid={`channel-status-${c.channel}`}
            >
              {!checked
                ? "Đang tắt"
                : total === 0
                  ? "● Đang bật"
                  : `● ${ready}/${total} sự kiện sẵn sàng`}
            </span>
            {/* #19667 D7 — the OTP is sent by its own path and ignores these switches (verified in
                AddyMessageOtpSender / OtpReadiness), so "Zalo off" while the OTP row reads READY on
                Zalo looked like a contradiction. Said here, where the switch is. */}
            {!checked && c.channel !== "EMAIL" && (
              <span className="text-xs text-muted-foreground" data-testid={`channel-otp-note-${c.channel}`}>
                Không áp cho mã OTP tra cứu kết quả — OTP bật/tắt riêng ở dòng của nó.
              </span>
            )}
            {note?.key === c.key && (
              <span
                role="status"
                className={
                  "text-xs " +
                  (note.ok ? "text-emerald-700" : "text-destructive")
                }
              >
                {note.text}
              </span>
            )}
          </div>
        );
      })}
    </div>
    </div>
  );
}
